import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  List,
  Popconfirm,
  Result,
  Row,
  Skeleton,
  Space,
  Steps,
  Tag,
  Typography,
  App as AntApp,
} from 'antd';
import { ArrowLeftOutlined, SendOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import {
  useOrderEventsQuery,
  useOrderQuery,
  useSuggestedTemplateQuery,
  useUpdateOrderMutation,
} from './hooks';
import { ALLOWED_TRANSITIONS, TIMELINE_FLOW } from './state-transitions';
import { OrderStatusTag } from './OrderStatusTag';
import { templateDisplayName } from '@/templates/displayName';
import { useCustomerQuery } from '@/customers/hooks';
import { useTenantSettingsQuery, useTenantBlocked } from '@/settings/hooks';
import { BlockedActionTooltip } from '@/components/BlockedActionTooltip';
import { deriveChannelAvailability } from '@/messaging/channelAvailability';
import { useMessagesQuery } from '@/messages/hooks';
import { ErrorAlert } from '@/components/ErrorAlert';
import { ApiError } from '@starling/core/api/errors';
import { formatDateTime, formatRelative } from '@/lib/format';
import { SendMessageDialog } from '@/components/SendMessageDialog';
import { MessageStatusTag } from '@/messages/MessageStatusTag';

const { Title, Text, Paragraph } = Typography;

export function OrderDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message, modal } = AntApp.useApp();

  const orderQuery = useOrderQuery(id);
  const eventsQuery = useOrderEventsQuery(id);
  const suggestedQuery = useSuggestedTemplateQuery(id);
  const updateMutation = useUpdateOrderMutation();
  const { isBlocked } = useTenantBlocked();

  const order = orderQuery.data;
  const customerQuery = useCustomerQuery(order?.customer_id);
  const customer = customerQuery.data;
  const tenantSettingsQuery = useTenantSettingsQuery();

  // Подгружаем сообщения по клиенту и на клиенте фильтруем по order_id.
  // Это компромисс: серверного фильтра `?order_id=` пока нет в M2 API,
  // но фильтр по customer_id урезает выборку до приемлемого размера.
  const messagesQuery = useMessagesQuery(
    order ? { page: 1, pageSize: 50, customerId: order.customer_id } : { page: 1, pageSize: 50 },
  );
  const orderMessages = (messagesQuery.data?.items ?? []).filter(
    (m) => order && m.order_id === order.id,
  );

  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [prefilledTemplateCode, setPrefilledTemplateCode] = useState<string | undefined>(undefined);

  if (orderQuery.isLoading) {
    return (
      <Card>
        <Skeleton active />
      </Card>
    );
  }

  if (orderQuery.isError) {
    const err = orderQuery.error;
    if (err instanceof ApiError && err.status === 404) {
      return (
        <Result
          status="404"
          title={t('order_detail.not_found')}
          extra={
            <Button type="primary" onClick={() => navigate('/orders')}>
              {t('order_detail.back_to_list')}
            </Button>
          }
        />
      );
    }
    return <ErrorAlert error={err} />;
  }

  if (!order || !id) return <ErrorAlert error={new Error(t('order_detail.not_found'))} />;

  const events = eventsQuery.data?.events ?? [];
  const reached = new Set(events.map((e) => e.to_status));
  const allowedNext = ALLOWED_TRANSITIONS[order.status];
  // Доступность каналов — общий хелпер (тот же в SendMessageDialog). Определяет
  // текст/состояние кнопки отправки: Telegram (linked) или SMS-fallback (unlinked
  // + SMS доступен). followups-m4 #1: раньше карточка блокировала отправку для
  // непривязанного, хотя backend умеет SMS-fallback заказного шаблона.
  const { isLinked, smsAvailable, hasLiveChannel } = deriveChannelAvailability(
    customer ?? null,
    tenantSettingsQuery.data ?? null,
  );
  const suggestedTemplate = suggestedQuery.data ?? null;

  const handleTransition = (next: typeof order.status): void => {
    updateMutation.mutate(
      { id, patch: { status: next } },
      {
        onSuccess: () => {
          void message.success(t('order_detail.status_changed'));
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.code === 'invalid_status_transition') {
              void message.error(t('order_detail.errors.invalid_transition'));
            } else {
              void message.error(err.message);
            }
          } else {
            void message.error(t('errors.generic'));
          }
        },
      },
    );
  };

  const openSendDialog = (templateCode?: string): void => {
    if (!customer) {
      void message.warning(t('order_detail.customer_loading'));
      return;
    }
    // Блокируем только если НЕТ ни одного канала (не привязан И SMS недоступен).
    // Если канал есть (Telegram или SMS-fallback) — открываем диалог, он сам
    // выберет канал и покажет превью/индикацию (followups-m4 #1).
    if (!hasLiveChannel) {
      modal.warning({
        title: t('order_detail.no_channel_title'),
        content: t('order_detail.no_channel_hint'),
        okText: t('order_detail.go_to_customer'),
        onOk: () => navigate(`/customers/${customer.id}`),
      });
      return;
    }
    setPrefilledTemplateCode(templateCode);
    setSendDialogOpen(true);
  };

  // Steps для timeline. Текущий статус = активный шаг.
  // `events.find(by to_status)` даёт нам метку времени для пройденного.
  const currentIndex = TIMELINE_FLOW.indexOf(
    order.status === 'cancelled' ? 'accepted' : order.status,
  );
  const stepsItems = TIMELINE_FLOW.map((s) => {
    const ev = events.find((e) => e.to_status === s);
    const description = ev ? formatDateTime(ev.created_at) : undefined;
    const item: { title: string; description?: string; status?: 'finish' | 'process' | 'wait' } = {
      title: t(`orders.status.${s}`),
    };
    if (description) item.description = description;
    if (reached.has(s)) {
      item.status = s === order.status ? 'process' : 'finish';
    } else {
      item.status = 'wait';
    }
    return item;
  });

  return (
    <div>
      <Link
        to="/orders"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16 }}
      >
        <ArrowLeftOutlined /> {t('order_detail.back_to_list')}
      </Link>

      {/* Header card */}
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space align="center" size="middle" wrap>
            <Text
              type="secondary"
              style={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 12 }}
            >
              {t('order_detail.header_overline')}
            </Text>
            <Title level={3} className="sl-mono" style={{ margin: 0 }}>
              {order.number}
            </Title>
            <OrderStatusTag status={order.status} />
          </Space>
          {order.note && <Paragraph type="secondary">{order.note}</Paragraph>}
          {customer && (
            <Space align="center" size="middle" wrap>
              <Link to={`/customers/${customer.id}`}>
                <Text strong>{customer.name}</Text>
              </Link>
              <span className="sl-mono" style={{ color: 'var(--ink-muted)' }}>
                {customer.phone}
              </span>
              {isLinked ? (
                <Tag color="cyan">{t('customers.linked_badge')}</Tag>
              ) : (
                <Tag>{t('customers.unlinked_badge')}</Tag>
              )}
            </Space>
          )}
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={15}>
          {/* Timeline */}
          <Card
            title={t('order_detail.timeline_title')}
            extra={<Text type="secondary">{t('order_detail.timeline_extra')}</Text>}
            style={{ marginBottom: 16 }}
          >
            {eventsQuery.isLoading && <Skeleton active paragraph={{ rows: 3 }} />}
            {eventsQuery.error && <ErrorAlert error={eventsQuery.error} />}
            {!eventsQuery.isLoading && (
              <>
                {order.status === 'cancelled' && (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t('orders.status.cancelled')}
                  />
                )}
                <Steps
                  direction="vertical"
                  current={currentIndex >= 0 ? currentIndex : 0}
                  items={stepsItems}
                />
                {allowedNext.length > 0 && (
                  <div
                    style={{
                      marginTop: 16,
                      paddingTop: 16,
                      borderTop: '1px solid var(--hairline)',
                    }}
                  >
                    <Text
                      type="secondary"
                      style={{
                        display: 'block',
                        marginBottom: 12,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        fontSize: 12,
                      }}
                    >
                      {t('order_detail.transition_to')}
                    </Text>
                    <BlockedActionTooltip>
                      <Space wrap>
                        {allowedNext.map((next) => (
                          <Popconfirm
                            key={next}
                            title={t('order_detail.confirm_transition', {
                              status: t(`orders.status.${next}`),
                            })}
                            okText={t('common.yes')}
                            cancelText={t('common.no')}
                            onConfirm={() => handleTransition(next)}
                            disabled={isBlocked}
                          >
                            <Button
                              danger={next === 'cancelled'}
                              type={next === 'cancelled' ? 'default' : 'default'}
                              loading={updateMutation.isPending}
                              disabled={isBlocked}
                            >
                              {t(`orders.status.${next}`)}
                            </Button>
                          </Popconfirm>
                        ))}
                      </Space>
                    </BlockedActionTooltip>
                  </div>
                )}
              </>
            )}
          </Card>

          {/* Messages по этому заказу */}
          <Card
            title={t('order_detail.messages_title')}
            extra={
              isLinked && (
                <Button type="primary" icon={<SendOutlined />} onClick={() => openSendDialog()}>
                  {t('order_detail.send_button')}
                </Button>
              )
            }
          >
            {messagesQuery.isLoading && <Skeleton active paragraph={{ rows: 2 }} />}
            {messagesQuery.error && <ErrorAlert error={messagesQuery.error} />}
            {!messagesQuery.isLoading && orderMessages.length === 0 && (
              <div style={{ padding: '24px 0', textAlign: 'center' }}>
                <Text type="secondary">{t('order_detail.no_messages')}</Text>
              </div>
            )}
            {orderMessages.length > 0 && (
              <List
                dataSource={orderMessages}
                renderItem={(m) => (
                  <List.Item>
                    <Space direction="vertical" style={{ width: '100%' }} size={4}>
                      <Space size={8} wrap>
                        <Tag>{m.channel}</Tag>
                        <MessageStatusTag status={m.status} />
                        <Text type="secondary" className="sl-mono" style={{ fontSize: 12 }}>
                          {formatDateTime(m.created_at)}
                        </Text>
                        {m.error_code && (
                          <Text type="danger" className="sl-mono" style={{ fontSize: 12 }}>
                            {m.error_code}
                          </Text>
                        )}
                      </Space>
                      <Text>{m.body}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} md={9}>
          {/* Suggested-template card */}
          {suggestedTemplate && (
            <Card
              style={{
                background: 'var(--health-turquoise-200)',
                borderColor: 'var(--health-turquoise-600)',
                marginBottom: 16,
              }}
            >
              <Text
                style={{
                  color: 'var(--health-turquoise-600)',
                  fontSize: 12,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                }}
              >
                {t('order_detail.recommended_overline')}
              </Text>
              <Title level={5} style={{ marginTop: 8, marginBottom: 4 }}>
                «{templateDisplayName(t, suggestedTemplate.code)}»
              </Title>
              <Text type="secondary" className="sl-mono" style={{ fontSize: 11 }}>
                {suggestedTemplate.code}
              </Text>
              <Paragraph type="secondary" style={{ marginTop: 8 }}>
                {t('order_detail.recommended_subtitle')}
              </Paragraph>
              {!isLinked && smsAvailable && (
                // Канал есть (SMS-fallback), но платный — предупреждаем на карточке.
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={t('send_dialog.info.unlinked_fallback_sms')}
                />
              )}
              {!isLinked && !smsAvailable && (
                // Канала нет вообще: не привязан И SMS недоступен (выкл/kill-switch).
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={t('send_dialog.errors.customer_not_linked')}
                  description={
                    customer && (
                      <Link to={`/customers/${customer.id}`}>
                        {t('order_detail.link_customer_link')}
                      </Link>
                    )
                  }
                />
              )}
              <Button
                type="primary"
                icon={<SendOutlined />}
                disabled={!hasLiveChannel}
                onClick={() => openSendDialog(suggestedTemplate.code)}
                block
              >
                {isLinked ? t('order_detail.send_via_telegram') : t('order_detail.send_via_sms')}
              </Button>
            </Card>
          )}

          {/* Details card */}
          <Card title={t('order_detail.details_title')}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('order_detail.created_at')}>
                <span className="sl-mono">{formatDateTime(order.created_at)}</span>
              </Descriptions.Item>
              <Descriptions.Item label={t('order_detail.updated_at')}>
                <span className="sl-mono">
                  {formatDateTime(order.updated_at)} ({formatRelative(order.updated_at)})
                </span>
              </Descriptions.Item>
              {customer && (
                <Descriptions.Item label={t('order_detail.consent_marketing')}>
                  {customer.consent_marketing ? (
                    <Tag color="green">{t('common.yes')}</Tag>
                  ) : (
                    <Tag>{t('common.no')}</Tag>
                  )}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {customer && (
        <SendMessageDialog
          open={sendDialogOpen}
          onClose={() => setSendDialogOpen(false)}
          customerId={customer.id}
          orderId={order.id}
          {...(prefilledTemplateCode ? { initialTemplateCode: prefilledTemplateCode } : {})}
          initialMode="template"
        />
      )}
    </div>
  );
}
